import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Package,
  Tag,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { productsService } from "../services/productsService";
import { companiesService } from "../services/companiesService";
import type { Company, Product } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { Button } from "../components/Button";
import { toast } from "sonner";
import { engagementService } from "../services/engagementService";
import { useTranslation } from "react-i18next";
import { QuickAnnotatePanel } from "../components/QuickAnnotatePanel";
import { scanApi } from "../api/scan";
import { SkeletonList } from "../components/SkeletonList";
import {
  newAnnotationNote,
  parseAnnotationNotes,
  serializeAnnotationNotes,
  type AnnotationNote,
} from "../utils/annotationNotes";
import { markLabel, CONTACT_MARK_TONE_CLASS, CONTACT_MARK_PRESETS } from "../utils/contactMarks";

export function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedEventId, savedContacts, updateSavedContact, updateScan, user } = useAppStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState<Product | undefined>();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [noteEntries, setNoteEntries] = useState<AnnotationNote[]>([]);
  const [scanAnnotation, setScanAnnotation] = useState<{
    scanId?: string;
    tags: string[];
  }>({ tags: [] });

  useEffect(() => {
    if (!id || !selectedEventId) {
      setProduct(undefined);
      setCompany(null);
      setIsFavorite(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const products = await productsService.getByEvent(selectedEventId);
        const found = products.find((p) => p.id === id);
        if (cancelled) return;
        setProduct(found);
        if (found) {
          const [comp, favorited] = await Promise.all([
            companiesService.getById(found.companyId, selectedEventId),
            productsService.isFavorite(selectedEventId, found.id).catch(() => false),
          ]);
          if (!cancelled) {
            setCompany(comp ?? null);
            setIsFavorite(favorited);
          }
        } else {
          setCompany(null);
          setIsFavorite(false);
        }
      } catch {
        if (!cancelled) {
          setProduct(undefined);
          setCompany(null);
          setIsFavorite(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, selectedEventId]);

  useEffect(() => {
    if (!product || !selectedEventId) {
      setScanAnnotation({ tags: [] });
      setNoteEntries([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { scans } = await scanApi.list(selectedEventId);
        if (cancelled) return;
        const hit = scans.find(
          (s) =>
            s.productId === product.id ||
            (s.scanTargetType === "product" && s.scanTargetId === product.id),
        );
        const local = savedContacts.find((c) => c.companyId === product.companyId);
        const rawNotes = hit?.notes ?? local?.notes ?? "";
        const tags = hit?.tags ?? local?.tags ?? [];
        setScanAnnotation({
          scanId: hit?.id,
          tags,
        });
        setNoteEntries(parseAnnotationNotes(rawNotes, tags));
      } catch {
        if (!cancelled) {
          const local = savedContacts.find((c) => c.companyId === product.companyId);
          const tags = local?.tags ?? [];
          setScanAnnotation({ tags });
          setNoteEntries(parseAnnotationNotes(local?.notes, tags));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product, selectedEventId, savedContacts]);

  useEffect(() => {
    if (!product || !selectedEventId) return;
    void engagementService.trackProductView(selectedEventId, product.id, product.companyId);
  }, [product?.id, product?.companyId, selectedEventId]);

  if (loading) {
    return (
      <PageShell>
        <PageHeader title={t("products.title")} back />
        <SkeletonList variant="detail" />
      </PageShell>
    );
  }

  if (!product) {
    return (
      <PageShell>
        <div className="pt-20 text-center">
          <div className="text-gray-500 mb-4">{t("products.not_found")}</div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
        </div>
      </PageShell>
    );
  }

  const handleViewCompany = () => {
    if (company) {
      navigate(`/companies/${company.id}`);
    }
  };

  const handleSave = async () => {
    if (!selectedEventId || favoriting) return;
    setFavoriting(true);
    try {
      if (isFavorite) {
        await productsService.removeFavorite(selectedEventId, product.id);
        setIsFavorite(false);
        toast.success(t("visitor.product_favorite_removed"));
      } else {
        await productsService.addFavorite(selectedEventId, product.id);
        setIsFavorite(true);
        toast.success(t("visitor.product_favorite_added"));
      }
    } catch {
      toast.error(t("visitor.favorite_update_failed"));
    } finally {
      setFavoriting(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <PageShell noPadding>
      <PageHeader
        title={product.name}
        subtitle={company?.name}
        back
        backTo="/products"
      />

      <div className="pb-24">
        {/* Image Gallery */}
        {product.images.length > 0 && (
          <div className="relative w-full aspect-[4/3] bg-gray-50">
            <img
              src={product.images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {product.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </button>
                {/* Indicator dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex
                          ? "bg-white w-6"
                          : "bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="px-4 pt-4 space-y-4">
          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                  <Package size={26} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[20px] text-gray-900 mb-1">{product.name}</h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {product.category?.trim() ? (
                      <p className="text-[13px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block">
                        {product.category}
                      </p>
                    ) : null}
                    {product.price?.trim() ? (
                      <p className="text-[13px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-block">
                        {product.price}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {product.description?.trim() ? (
                <p className="text-[14px] text-gray-600 leading-relaxed">
                  {product.description}
                </p>
              ) : null}

              {/* Tags */}
              {product.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100 text-[11px] text-gray-600"
                    >
                      <Tag size={10} />
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>

          {/* Notes only after product QR scan — no composer from catalog browse. */}
          {product && scanAnnotation.scanId ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.4 }}
            >
              <GlassCard variant="default">
                {noteEntries.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {noteEntries.map((note) => (
                      <div
                        key={note.id}
                        className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2.5"
                      >
                        {note.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {note.tags.map((tagId) => {
                              const preset = CONTACT_MARK_PRESETS.find((p) => p.id === tagId);
                              const tone = preset
                                ? CONTACT_MARK_TONE_CLASS[preset.tone]
                                : CONTACT_MARK_TONE_CLASS.slate;
                              return (
                                <span
                                  key={tagId}
                                  className={`px-2 py-0.5 rounded-full border text-[11px] ${tone.on}`}
                                >
                                  {markLabel(tagId, t)}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                        {note.text ? (
                          <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{note.text}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <QuickAnnotatePanel
                  key={`${product.id}-${scanAnnotation.scanId}-composer`}
                  initialNotes=""
                  initialTags={[]}
                  title={t("annotate.title")}
                  placeholder={t("annotate.placeholder_product")}
                  saveLabel={t("annotate.save")}
                  presets={[
                    { id: "important", label: t("marks.product_new"), tone: "amber" },
                    { id: "urgent", label: t("marks.product_unique"), tone: "rose" },
                    { id: "hot", label: t("marks.product_ru"), tone: "orange" },
                    { id: "ski", label: t("marks.product_study"), tone: "slate" },
                    { id: "followup", label: t("marks.followup"), tone: "sky" },
                  ]}
                  onSave={async (values) => {
                    if (!selectedEventId || !scanAnnotation.scanId) {
                      toast.error(t("annotate.save_failed"));
                      throw new Error("scan_required");
                    }
                    const hitId = scanAnnotation.scanId;
                    try {
                      const nextNotes = [
                        ...noteEntries,
                        newAnnotationNote(values.notes, values.tags),
                      ];
                      const serialized = serializeAnnotationNotes(nextNotes);
                      const allTags = [...new Set(nextNotes.flatMap((n) => n.tags))];
                      await scanApi.updateNotes(selectedEventId, hitId, {
                        notes: serialized,
                        tags: allTags,
                      });
                      updateSavedContact(product.companyId, {
                        notes: serialized,
                        tags: allTags,
                        eventId: selectedEventId,
                        userId: user.id,
                      });
                      updateScan(hitId, { notes: serialized, tags: allTags });
                      setScanAnnotation({ scanId: hitId, tags: allTags });
                      setNoteEntries(nextNotes);
                      toast.success(t("annotate.saved"));
                    } catch (err) {
                      toast.error(t("annotate.save_failed"));
                      throw err;
                    }
                  }}
                />
              </GlassCard>
            </motion.div>
          ) : null}

          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
            >
              <GlassCard variant="default">
                <h3 className="text-[14px] text-gray-900 mb-3">{t("visitor.specifications")}</h3>
                <div className="space-y-2">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-start justify-between text-[13px] pb-2 border-b border-gray-100 last:border-0"
                    >
                      <span className="text-gray-500">{key}</span>
                      <span className="text-gray-900 text-right font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Company Info */}
          {company && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
            >
              <GlassCard variant="default">
                <h3 className="text-[14px] text-gray-900 mb-3">{t("visitor.manufacturer")}</h3>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center flex-shrink-0">
                    <Building2 size={20} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] text-gray-900 mb-1">{company.name}</div>
                    <div className="text-[12px] text-gray-500">{company.category}</div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={handleViewCompany}
                  className="w-full mt-3"
                >
                  <Building2 size={16} />
                  {t("products.go_to_company")}
                </Button>
              </GlassCard>
            </motion.div>
          )}

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <Button
              variant={isFavorite ? "secondary" : "primary"}
              onClick={handleSave}
              disabled={favoriting}
              className="w-full"
            >
              <Package size={18} />
              {favoriting
                ? t("visitor.saving")
                : isFavorite
                  ? t("products.in_favorites")
                  : t("products.save_favorite")}
            </Button>
          </motion.div>
        </div>
      </div>
    </PageShell>
  );
}
